<script setup lang="ts">
  import { computed } from 'vue';

  import { DocumentSendAudienceType } from '@/entities/DocumentSend';
  import type { ProtheusClient, ProtheusSupplier } from '@/entities/Protheus';

  import type { ElectronicRecipientRow } from '../model/electronicRecipient';
  import DocumentSendElectronicRecipientGroup from './DocumentSendElectronicRecipientGroup.vue';

  const props = withDefaults(
    defineProps<{
      audienceType: DocumentSendAudienceType | '';
      clientRecipients: ElectronicRecipientRow[];
      supplierRecipients: ElectronicRecipientRow[];
      clientOptions: ProtheusClient[];
      supplierOptions: ProtheusSupplier[];
      errorMessage?: string;
      isLoading?: boolean;
    }>(),
    {
      errorMessage: '',
      isLoading: false,
    }
  );

  const emit = defineEmits<{
    (e: 'update:clientRecipients', value: ElectronicRecipientRow[]): void;
    (e: 'update:supplierRecipients', value: ElectronicRecipientRow[]): void;
  }>();

  const showsClientGroup = computed(
    () =>
      props.audienceType === DocumentSendAudienceType.CLIENT ||
      props.audienceType === DocumentSendAudienceType.CLIENT_SUPPLIER
  );

  const showsSupplierGroup = computed(
    () =>
      props.audienceType === DocumentSendAudienceType.SUPPLIER ||
      props.audienceType === DocumentSendAudienceType.CLIENT_SUPPLIER
  );

  const updateClientRecipients = (rows: ElectronicRecipientRow[]) => {
    emit('update:clientRecipients', rows);
  };

  const updateSupplierRecipients = (rows: ElectronicRecipientRow[]) => {
    emit('update:supplierRecipients', rows);
  };
</script>

<template>
  <section
    class="flex flex-col gap-4 p-6"
    data-testid="electronic-recipients-section"
  >
    <div class="flex flex-col gap-1">
      <span class="text-sm font-semibold text-gray-07">Selecione os destinatários:</span>
    </div>

    <div class="flex gap-6">
      <DocumentSendElectronicRecipientGroup
        v-if="showsClientGroup"
        class="w-1/2"
        title="Clientes"
        empty-label="Nenhum cliente encontrado."
        :options="clientOptions"
        recipient-type="CLIENT"
        :selected-recipients="clientRecipients"
        :is-loading="isLoading"
        group-test-id="client-recipients-group"
        row-test-id="client-recipient-row"
        select-all-test-id="client-select-all"
        total-selected-test-id="client-selected-total"
        @update:selected-recipients="updateClientRecipients"
      />

      <DocumentSendElectronicRecipientGroup
        v-if="showsSupplierGroup"
        class="w-1/2"
        title="Fornecedores"
        empty-label="Nenhum fornecedor encontrado."
        :options="supplierOptions"
        recipient-type="SUPPLIER"
        :selected-recipients="supplierRecipients"
        :is-loading="isLoading"
        group-test-id="supplier-recipients-group"
        row-test-id="supplier-recipient-row"
        select-all-test-id="supplier-select-all"
        total-selected-test-id="supplier-selected-total"
        @update:selected-recipients="updateSupplierRecipients"
      />
    </div>

    <p
      v-if="props.errorMessage"
      class="text-xs text-red-16"
      data-testid="electronic-recipient-error"
    >
      {{ props.errorMessage }}
    </p>
  </section>
</template>
